import * as React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../../contexts/LanguageContext';

export interface RtlButtonProps extends Omit<MuiButtonProps, 'startIcon' | 'endIcon'> {
  /**
   * Icon to display with the button.
   * In LTR mode (English): Icon appears on the right side of text
   * In RTL mode (Arabic): Icon appears on the left side of text
   */
  startIcon?: React.ReactNode;
  /**
   * Icon to display with the button.
   * In LTR mode (English): Icon appears on the left side of text
   * In RTL mode (Arabic): Icon appears on the right side of text
   */
  endIcon?: React.ReactNode;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'isRtl',
})<{ isRtl?: boolean }>(({ isRtl }) => ({
  '& .MuiButton-endIcon': {
    marginLeft: isRtl ? 0 : undefined,
    marginRight: isRtl ? 4 : undefined,
  },
}));

/**
 * RTL-aware Button component that automatically switches icon positions
 * based on the current language direction.
 *
 * In LTR mode (English): Text on left, Icon on right
 * In RTL mode (Arabic): Text on right, Icon on left
 */
export default function RtlButton(props: RtlButtonProps) {
  const { startIcon, endIcon, ...otherProps } = props;
  const { direction } = useLanguage();

  const isRtl = direction === 'rtl';

  return (
    <StyledButton
      {...otherProps}
      isRtl={isRtl}
      endIcon={startIcon}
    />
  );
}

